import axios from 'axios';
import { toast } from 'react-hot-toast';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000',
  timeout: 10000,
  withCredentials: true
});

// Add a request interceptor to add the auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add cache control headers for better caching
    if (config.method === 'get') {
      config.headers['Cache-Control'] = 'max-age=300'; // 5 minutes cache
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle various backend responses
axiosInstance.interceptors.response.use(
  (response) => {
    // Log cache information if available
    if (response.data?.source === 'cache') {
      console.log('✅ Data served from cache');
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle rate limiting
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 60;
      toast.error(`Too many requests. Please wait ${retryAfter} seconds before trying again.`);
      return Promise.reject(error);
    }

    // Handle 401 errors and token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/users/refresh-token`,
          { refreshToken },
          { withCredentials: true }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        
        // Update tokens in localStorage
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        // Update the Authorization header
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (error) {
        // If refresh token fails, log the user out
        console.error('Token refresh failed:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        toast.error('Your session has expired. Please log in again.');
        return Promise.reject(error);
      }
    }

    // Handle other errors
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error.message) {
      toast.error(error.message);
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const api = {
  // User authentication
  register: (userData) => axiosInstance.post('/api/v1/users/registerUser', userData),
  login: (credentials) => axiosInstance.post('/api/v1/users/loginUser', credentials),
  googleLogin: (googleData) => axiosInstance.post('/api/v1/users/google-login', googleData),
  logout: () => axiosInstance.post('/api/v1/users/logoutUser'),
  getCurrentUser: () => axiosInstance.get('/api/v1/users/current-user'),
  updateProfile: (userData) => axiosInstance.put('/api/v1/users/update-account', userData),
  changePassword: (passwordData) => axiosInstance.post('/api/v1/users/change-password', passwordData),
  
  // Blog operations
  getAllBlogs: (forceRefresh = false) => {
    const url = forceRefresh ? '/api/v1/users/getAllblogs?refresh=true' : '/api/v1/users/getAllblogs';
    return axiosInstance.get(url);
  },
  getBlogById: (id) => axiosInstance.get(`/api/v1/users/blog/${id}`),
  createBlog: (blogData) => axiosInstance.post('/api/v1/users/createBlog', blogData),
  updateBlog: (blogData) => axiosInstance.put('/api/v1/users/updateBlog', blogData),
  deleteBlog: (id) => axiosInstance.delete(`/api/v1/users/deleteBlog/${id}`),
  likeBlog: (blogId) => axiosInstance.put('/api/v1/users/likeBlog', { blogId }),
  addComment: (commentData) => axiosInstance.post('/api/v1/users/commentBlog', commentData),
  getComments: (blogId) => axiosInstance.get(`/api/v1/users/comments/${blogId}`),
  deleteComment: (commentId) => axiosInstance.delete(`/api/v1/users/comment/${commentId}`),
  getBlogStats: () => axiosInstance.get('/api/v1/users/blog-stats'),
  
  // Payment operations
  createPaymentIntent: (paymentData) => axiosInstance.post('/api/v1/payment/create-payment-intent', paymentData),
  confirmPayment: (paymentData) => axiosInstance.post('/api/v1/payment/confirm-payment', paymentData),
  getPaymentHistory: () => axiosInstance.get('/api/v1/payment/payment-history'),
  getPaymentStats: () => axiosInstance.get('/api/v1/payment/payment-stats'),
  createSubscription: (subscriptionData) => axiosInstance.post('/api/v1/payment/create-subscription', subscriptionData),
  cancelSubscription: (subscriptionId) => axiosInstance.delete(`/api/v1/payment/cancel-subscription/${subscriptionId}`),
  
  // Health check
  healthCheck: () => axiosInstance.get('/health'),
  
  // Sports data (existing endpoints)
  getSportsData: () => axiosInstance.get('/api/v1/sports'),
  updateMatchData: (sportType, data) => axiosInstance.post('/api/v1/sports/update', { sportType, data }),
};

export default axiosInstance;
